import React, {useEffect, useMemo, useState} from 'react';
import classes from './topup-modal.module.css';
import {cancelWebSubscription, createWebPaymentLink, getWebProfile} from '../../../utils/api';
import {getTopupPresetsForTariff} from '../../../utils/topupPresets';

const TopupModal = ({open, onClose, onProfileRefresh}) => {
    const [profile, setProfile] = useState(null);
    const {presets, defaultAmount} = useMemo(
        () => getTopupPresetsForTariff(profile?.tariff_name),
        [profile?.tariff_name],
    );
    const [amount, setAmount] = useState(150);
    /** oneoff | subscription — переход к оплате только по кнопке «Пополнить» */
    const [payMode, setPayMode] = useState('oneoff');
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        if (!open) return;
        setPayMode('oneoff');
        setLoading(false);
        setErrorText('');
        let cancelled = false;
        (async () => {
            try {
                const p = await getWebProfile();
                if (!cancelled) setProfile(p);
            } catch {
                if (!cancelled) setProfile(null);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setAmount(defaultAmount);
    }, [open, defaultAmount]);

    const monthlyRub = profile?.subscription_monthly_rub;
    const hasSubscription = Boolean(profile?.subscription && String(profile.subscription).trim());

    const canPay =
        payMode === 'subscription'
            ? monthlyRub != null && monthlyRub >= 1
            : Number.isFinite(Number(amount)) && Number(amount) >= 100;

    async function onSubmit() {
        if (payMode === 'subscription') {
            if (monthlyRub == null || monthlyRub < 1) {
                setErrorText('Подписка недоступна для вашего тарифа');
                return;
            }
            setLoading(true);
            setErrorText('');
            try {
                const url = await createWebPaymentLink(0, { isMonth: true });
                window.open(url, '_blank', 'noopener,noreferrer');
                onClose();
            } catch (err) {
                setErrorText(err?.message || 'Не удалось создать ссылку на подписку');
            } finally {
                setLoading(false);
            }
            return;
        }
        const val = Number(amount);
        if (!Number.isFinite(val) || val < 100) {
            setErrorText('Минимальная сумма пополнения - 100р');
            return;
        }
        setLoading(true);
        setErrorText('');
        try {
            const url = await createWebPaymentLink(val, { isMonth: false });
            window.open(url, '_blank', 'noopener,noreferrer');
            onClose();
        } catch (err) {
            setErrorText(err?.message || 'Не удалось создать ссылку на оплату');
        } finally {
            setLoading(false);
        }
    }

    async function onCancelSubscription() {
        setLoading(true);
        setErrorText('');
        try {
            await cancelWebSubscription();
            const p = await getWebProfile();
            setProfile(p);
            if (onProfileRefresh) onProfileRefresh();
            onClose();
        } catch (err) {
            setErrorText(err?.message || 'Не удалось отменить подписку');
        } finally {
            setLoading(false);
        }
    }

    if (!open) return null;

    return (
        <div className={classes.modalOverlay} onClick={onClose} role="dialog" aria-modal="true">
            <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
                <div className={classes.modalHeader}>
                    <div className={classes.modalTitle}>Пополнение баланса</div>
                    <button type="button" className={classes.closeBtn} onClick={onClose}>
                        Закрыть
                    </button>
                </div>
                <div className={classes.topupBody}>
                    {hasSubscription ? (
                        <>
                            <div className={classes.topupHint}>
                                У вас активирована ежемесячная подписка. Списание — по тарифу
                                {monthlyRub != null ? ` (${monthlyRub}₽ при текущем тарифе)` : ''}.
                            </div>
                            <button type="button" className={classes.topupSubmitBtn} disabled={loading} onClick={onCancelSubscription}>
                                {loading ? 'Отмена...' : 'Отменить подписку'}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className={classes.topupHint}>
                                Введите сумму в рублях или выберите один из предложенных вариантов ниже.
                            </div>
                            <input
                                className={classes.topupInput}
                                type="number"
                                min={100}
                                value={amount}
                                onChange={(e) => {
                                    setPayMode('oneoff');
                                    setAmount(e.target.value === '' ? '' : Number(e.target.value));
                                }}
                            />
                            <div className={classes.topupOptions}>
                                {monthlyRub != null && monthlyRub >= 1 && (
                                    <button
                                        type="button"
                                        className={`${classes.topupOptionBtn} ${payMode === 'subscription' ? classes.topupOptionActive : ''}`}
                                        onClick={() => {
                                            setPayMode('subscription');
                                            setAmount(monthlyRub);
                                        }}
                                        disabled={loading}
                                    >
                                        <span className={classes.optionMain}>Подписка {monthlyRub}₽</span>
                                        <span className={classes.optionSub}>ежемесячно (по тарифу)</span>
                                    </button>
                                )}
                                {presets.map((item) => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={`${classes.topupOptionBtn} ${payMode === 'oneoff' && Number(amount) === item.value ? classes.topupOptionActive : ''}`}
                                        onClick={() => {
                                            setPayMode('oneoff');
                                            setAmount(item.value);
                                        }}
                                    >
                                        <span className={classes.optionMain}>
                                            {item.value}р
                                            {item.bonus ? <span className={classes.topupBonus}> + {item.bonus}р</span> : null}
                                        </span>
                                        <span className={classes.optionSub}>{item.months}</span>
                                    </button>
                                ))}
                            </div>
                            <div className={classes.topupHelp}>
                                Если у вас появились вопросы по пополнению баланса, обратитесь к администратору
                                {' '}
                                <a href="https://t.me/easyfastvpn" target="_blank" rel="noreferrer" className={classes.topupLink}>@EasyFastVPN</a>
                            </div>
                            {errorText ? <div className={classes.errorText}>{errorText}</div> : null}
                            <button type="button" className={classes.topupSubmitBtn} disabled={loading || !canPay} onClick={onSubmit}>
                                {loading ? 'Создаем ссылку...' : 'Пополнить'}
                            </button>
                        </>
                    )}
                    {hasSubscription && errorText ? <div className={classes.errorText}>{errorText}</div> : null}
                </div>
            </div>
        </div>
    );
};

export default TopupModal;
